import { SpreadGroundImage } from 'editor/src/store/design/types';

import loadImage from 'editor/src/util/loadImage';

// Ground (background / foreground) images are awaited while a preview is built,
// and previews recompute on every edit. `loadImage` retries 3 times over ~3.6s
// before giving up, so an unreachable ground — an expired URL, or a bucket that
// serves no CORS header — used to cost that full ladder on every recompute, once
// per spread, and the preview queue renders spreads one at a time: three
// unreachable spreads stalled the bottom strip and the live preview for ~11s,
// again on every edit.
//
// Caching the settled promise per url fixes both halves: concurrent renderers
// share one in-flight load, and a url is only ever attempted once. This mirrors
// how spread masks are already loaded (see `loadSpreadMaskClipPath`) — including
// the trade-off, that a ground which fails stays absent until the next reload.
const cache = new Map<string, Promise<HTMLImageElement | undefined>>();

const loadGroundImage = (url: string): Promise<HTMLImageElement | undefined> => {
  const cached = cache.get(url);
  if (cached) {
    return cached;
  }

  const promise = loadImage(url, 'anonymous', {
    executor: 'addSpreadImage',
  }).catch(() => undefined);
  cache.set(url, promise);
  return promise;
};

/**
 * Start loading grounds as soon as they reach the store instead of lazily on the
 * first preview. Unreachable ones then burn their retry ladders in parallel,
 * during load, rather than serially in front of the user's first edit.
 */
export const prefetchGroundImages = (images: SpreadGroundImage[]) => {
  images.forEach((image) => {
    [image.url, image.thumbnailUrl].forEach((url) => {
      if (url) {
        void loadGroundImage(url);
      }
    });
  });
};

export const resetGroundImageCache = () => {
  cache.clear();
};

export default loadGroundImage;
